import type { Analytics } from "firebase/analytics";
import { initializeAnalytics, logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { FirebaseError, initializeApp } from "firebase/app";
import { getPerformance } from "firebase/performance";
import type { RemoteConfig } from "firebase/remote-config";
import { getRemoteConfig } from "firebase/remote-config";
import { VERSION } from "../../version";
import type { UserRole } from "../auth/auth";
import { isProdHostname } from "./isProd";

const DEV_ENV = {
  apiKey: "AIzaSyBOserZom7XjNeE58xpAvRkwCTPTuwKYF0",
  authDomain: "przezmorze-app.firebaseapp.com",
  databaseURL: "https://przezmorze-app.firebaseio.com",
  projectId: "przezmorze-app",
  storageBucket: "przezmorze-app.appspot.com",
  messagingSenderId: "749478107218",
  appId: "1:749478107218:web:a7461ff5b3865b28e2278b",
};

const PROD_ENV = {
  apiKey: "AIzaSyDSiZMziM-VV9PyIPKVFNis6OUL9OGfBzo",
  authDomain: "przezmorze-pl.firebaseapp.com",
  databaseURL: "https://przezmorze-pl.firebaseio.com",
  projectId: "przezmorze-pl",
  storageBucket: "przezmorze-pl.appspot.com",
  messagingSenderId: "593372701638",
  appId: "1:593372701638:web:9e0b5f8a1271ec75a588c7",
};

let analytics: Analytics;
let remoteConfig: RemoteConfig | null = null;

export function initFirebase() {
  const isProd = isProdHostname();
  if (!isProd) {
    console.info("Connecting to DEV Firebase environment");
  }
  const config = isProd ? PROD_ENV : DEV_ENV;
  const app = initializeApp(config);
  analytics = initializeAnalytics(app);
  setUserProperties(analytics, {
    app_version: VERSION,
  });
  getPerformance(app);
  remoteConfig = getRemoteConfig(app);
}

export function getRemoteConfigInstance(): RemoteConfig | null {
  return remoteConfig;
}

export function setUserIdForAnalytics(userId: string | null) {
  setUserId(analytics, userId);
}

export function setUserRoleForAnalytics(userRole: UserRole | null) {
  setUserProperties(analytics, { user_role: userRole });
}

export function logPageView(location: { pathname: string; search: string }) {
  if (analytics) {
    logEvent(analytics, "page_view", { page_path: location.pathname + location.search });
  } else {
    console.info("Google Analytics is not initialized. Skipping page view event");
  }
}

function logProductPurchase(
  transactionId: string | undefined,
  itemId: string,
  value: number | undefined,
  currency: string,
  index: number,
) {
  if (analytics) {
    logEvent(analytics, "purchase", {
      transaction_id: transactionId,
      value,
      currency,
      item_id: itemId,
      index,
    });
  } else {
    console.info("Google Analytics is not initialized. Skipping purchase event");
  }
}

export function logPurchase(
  transactionId: string | undefined,
  items: { readonly id: string; readonly value: number | undefined; readonly currency: string }[],
) {
  try {
    items.forEach(({ id, value, currency }, idx) => logProductPurchase(transactionId, id, value, currency, idx));
  } catch (e) {
    if (e instanceof FirebaseError) {
      console.info(`Firebase is not initialized. Skipping purchase with transaction ID ${transactionId}`);
    } else {
      throw e;
    }
  }
}

export function logLoginEvent(method: "google" | "email_and_password" | "email_link" | "travel360") {
  if (analytics) {
    logEvent(analytics, "login", { method });
  } else {
    console.warn("Google Analytics is not initialized. Skipping login event");
  }
}

export function logSignUpEvent(method: "google" | "email_and_password" | "email_link" | "travel360") {
  if (analytics) {
    logEvent(analytics, "sign_up", { method });
  } else {
    console.warn("Google Analytics is not initialized. Skipping sign up event");
  }
}
